import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { Link } from 'react-router-dom'
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { useAuthUser } from 'use-eazy-auth'
import Header from '../../components/Header'
import Page from '../../components/Page'
import Paginator from '../../components/Paginator'
import Sorter, { SortControl } from '../../components/Sorter'
import StickyTable from '../../components/StickyTable'
import {
  useAuthors,
  useAuthorsForSelect,
  useDeleteAuthor,
  useUnificaAuthors,
} from '../../hooks/authors'
import { useQsFilters } from '../../hooks/filters'
import { Author, User } from '../../types'
import { Field, Formik } from 'formik'
import { MultiSelectField, ReactSelectField } from '../../components/form'

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get('page') ?? 1),
  search: params.get('search') ?? '',
  ordering: params.get('ordering') ?? '',
})

export default function AuthorsList() {
  const { filters, uiFilters, setFiltersDebounced, setFilters } =
    useQsFilters(initFilters)
  const { data } = useAuthors(filters)
  const { user } = useAuthUser<User>()
  const deleteAuthor = useDeleteAuthor()
  const [modalRm, modalRmActions] = useModalTrigger<Author>()
  const [modalUnifica, modalUnificaActions] = useModalTrigger()

  const { data: authors } = useAuthorsForSelect()
  const unificaAutori = useUnificaAuthors()

  return (
    <Page padded>
      <Header>
        <div className="d-flex align-items-end">
          <h2 className="m-0">Autori</h2>
        </div>
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={modalUnificaActions.toggle}
          >
            <i className="bi bi-universal-access me-2"></i> Unifica
          </button>
          {user!.is_superuser && (
            <Link className="btn btn-primary" to="/authors/new">
              <i className="bi bi-plus-circle me-2"></i> Aggiungi
            </Link>
          )}
        </div>
      </Header>
      <div className="d-flex justify-content-between mt-3 mb-3 align-items-center">
        <input
          placeholder="Cerca"
          className="form-control w-auto"
          type="text"
          value={uiFilters.search}
          onChange={(e) =>
            setFiltersDebounced({ search: e.target.value, page: 1 })
          }
        />
      </div>
      <StickyTable className="flex-1">
        <thead>
          <Sorter
            value={uiFilters.ordering}
            onSortChange={(ordering) =>
              setFilters({
                ordering,
                page: 1,
              })
            }
          >
            <tr>
              <th>#</th>
              <th>
                <div className="d-flex align-items-center">
                  Nome
                  <SortControl field="name" />
                </div>
              </th>
              <th>
                <div className="d-flex align-items-center">
                  Data di nascita <SortControl field="birth_date" />
                </div>
              </th>
              <th>
                <div className="d-flex align-items-center">
                  Data di morte <SortControl field="death_date" />
                </div>
              </th>
              <th></th>
            </tr>
          </Sorter>
        </thead>
        <tbody>
          {data!.results.map((author) => (
            <tr key={author.id}>
              <td>{author.id}</td>
              <td>
                <Link to={`/authors/${author.id}`}>{author.name}</Link>
              </td>
              <td>{author.birth_date}</td>
              <td>{author.death_date}</td>
              <td>
                <i
                  onClick={() => modalRmActions.open(author)}
                  className="bi cursor-pointer text-danger bi-trash3-fill"
                ></i>
              </td>
            </tr>
          ))}
        </tbody>
      </StickyTable>
      <Modal centered isOpen={modalRm.isOpen} toggle={modalRmActions.toggle}>
        <ModalHeader toggle={modalRmActions.toggle}>Elimina</ModalHeader>
        <ModalBody>
          {modalRm.value && (
            <div className="pt-3 pb-3">
              Sei sicuro di voler eliminare l'autore {modalRm.value.name} ?
            </div>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <button
            onClick={modalRmActions.toggle}
            className="btn btn-outline-primary"
          >
            Annulla
          </button>
          <button
            className="btn  btn-danger"
            disabled={deleteAuthor.isLoading}
            onClick={async () => {
              await deleteAuthor.mutateAsync(modalRm.value!.id)
              modalRmActions.close()
            }}
          >
            <i className="bi bi-trash3-fill"></i> Elimina
          </button>
        </ModalFooter>
      </Modal>
      <div className="position-relative">
        <Paginator
          count={data!.count}
          currentPage={uiFilters.page}
          goToPage={(page) => setFilters({ page })}
        />
      </div>
      <Modal
        centered
        isOpen={modalUnifica.isOpen}
        toggle={modalUnificaActions.toggle}
      >
        <ModalHeader toggle={modalUnificaActions.toggle}>
          Unifica autori
        </ModalHeader>
        <Formik
          initialValues={{
            author_to_keep: null,
            authors_to_delete: [],
          }}
          onSubmit={async (values) => {
            if (!values.author_to_keep || values.authors_to_delete.length === 0)
              return
            await unificaAutori
              .mutateAsync({
                author_to_keep: values.author_to_keep,
                authors_to_delete: values.authors_to_delete,
              })
              .then(() => {
                modalUnificaActions.toggle()
              })
          }}
        >
          {({ values, handleChange, handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              <ModalBody>
                <div>
                  <Field
                    component={ReactSelectField}
                    name="author_to_keep"
                    label="Autore da mantenere"
                    placeholder="Seleziona l'autore da mantenere"
                    options={authors?.map((author) => ({
                      value: author.id,
                      label: author.name,
                    }))}
                  />
                </div>
                <div>
                  <Field
                    component={MultiSelectField}
                    name="authors_to_delete"
                    placeholder="Seleziona gli autori da eliminare"
                    label="Autori da eliminare"
                    options={authors
                      ?.filter((author) => author.id !== values.author_to_keep)
                      .map((author) => ({
                        value: author.id,
                        label: author.name,
                      }))}
                    isMulti
                  />
                </div>
              </ModalBody>
              <ModalFooter className="d-flex justify-content-between">
                <button
                  onClick={modalUnificaActions.toggle}
                  className="btn btn-outline-primary"
                >
                  Annulla
                </button>
                <button className="btn btn-success" type="submit">
                  <i className="bi bi-save-fill me-1"></i>
                  Salva
                </button>
              </ModalFooter>
            </form>
          )}
        </Formik>
      </Modal>
    </Page>
  )
}
